import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { CrisisFooter } from '@/components/CrisisFooter';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import {
  useSettingsData,
  useSubscriptionActions,
  useReferralActions,
  useAccountActions
} from '@/hooks/useSettingsHandlers';

import {
  SettingsHeader,
  SubscriptionSection,
  ReferralSection,
  NotificationSection,
  SecuritySection,
  SageStyleSection,
  DataSection,
  DangerZoneSection,
  CancelSubscriptionModal
} from '@/components/settings';

export const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();

  // Modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Data + action hooks
  const { twoFaEnabled, setTwoFaEnabled, referralStats } = useSettingsData(token);
  const subscription = useSubscriptionActions(token, user);
  const referral = useReferralActions(token, referralStats);
  const account = useAccountActions(token, { twoFaEnabled, setTwoFaEnabled, logout, navigate });

  // Push notifications
  const {
    isSupported: pushSupported,
    isSubscribed: pushSubscribed,
    isLoading: pushLoading,
    permission: pushPermission,
    subscribe: subscribePush,
    unsubscribe: unsubscribePush
  } = usePushNotifications(token);

  const handleTogglePush = async () => {
    if (pushSubscribed) {
      const success = await unsubscribePush();
      if (success) toast.success('Push notifications disabled');
      else toast.error('Failed to disable notifications');
      return;
    }
    const success = await subscribePush();
    if (success) {
      toast.success('Push notifications enabled');
    } else if (pushPermission === 'denied') {
      toast.error('Notifications blocked. Please enable in browser settings.');
    } else {
      toast.error('Failed to enable notifications');
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FBF9F6' }}>
      <div className="max-w-2xl mx-auto px-4 py-8 pb-32">
        <SettingsHeader />

        <div className="space-y-6">
          <SubscriptionSection
            user={user}
            onAddPayment={subscription.handleAddPayment}
            addingPayment={subscription.addingPayment}
            onManageSubscription={subscription.handleManageSubscription}
            openingPortal={subscription.openingPortal}
            onResubscribe={() => navigate('/checkout')}
            onOpenCancelModal={() => setShowCancelModal(true)}
            onResume={subscription.handleResumeSubscription}
            onUndoCancel={subscription.handleUndoCancel}
            resuming={subscription.resuming}
            undoingCancel={subscription.undoingCancel}
          />

          <ReferralSection
            referralStats={referralStats}
            onCopyCode={referral.handleCopyCode}
            copySuccess={referral.copySuccess}
            inviteEmail={referral.inviteEmail}
            setInviteEmail={referral.setInviteEmail}
            onSendInvite={referral.handleSendInvite}
            sendingInvite={referral.sendingInvite}
          />

          <SageStyleSection token={token} />

          <NotificationSection
            pushSupported={pushSupported}
            pushSubscribed={pushSubscribed}
            pushLoading={pushLoading}
            pushPermission={pushPermission}
            onTogglePush={handleTogglePush}
          />

          <SecuritySection
            twoFaEnabled={twoFaEnabled}
            toggling2fa={account.toggling2fa}
            onToggle2fa={account.handleToggle2fa}
          />

          <DataSection
            exporting={account.exporting}
            onExportData={account.handleExportData}
          />

          <DangerZoneSection
            showDeleteModal={showDeleteModal}
            setShowDeleteModal={setShowDeleteModal}
            deleteConfirmText={deleteConfirmText}
            setDeleteConfirmText={setDeleteConfirmText}
            deletePassword={deletePassword}
            setDeletePassword={setDeletePassword}
            onDeleteAccount={() => account.handleDeleteAccount(deleteConfirmText, deletePassword)}
            deleting={account.deleting}
            userEmail={user?.email}
          />
        </div>
      </div>

      <CancelSubscriptionModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onCancel={(data) => subscription.handleCancelSubscription(data, () => setShowCancelModal(false))}
        onPause={(data) => subscription.handlePauseSubscription(data, () => setShowCancelModal(false))}
        canceling={subscription.canceling}
        pausing={subscription.pausing}
      />

      <CrisisFooter />
    </div>
  );
};
